"use client"

import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

const styles = [
    {
        id: "modern-minimalist",
        title: "Modern Minimalist",
        description: "Clean lines, neutral palettes, and functional beauty define this timeless aesthetic",
        heroImage: "/images/interior/modern-minimalist-living-room-with-clean-lines.jpg",
        gallery: [
            "https://images.pexels.com/photos/7061394/pexels-photo-7061394.jpeg",
            "https://images.pexels.com/photos/276724/pexels-photo-276724.jpeg",
            "https://images.pexels.com/photos/271816/pexels-photo-271816.jpeg"
        ],
        features: ["Clean Lines", "Neutral Colors", "Functional Design"],
        color: "bg-slate-500",
        accentColor: "text-slate-600"
    },
    {
        id: "classic-elegance",
        title: "Classic Elegance",
        description: "Timeless sophistication with refined details and luxurious materials",
        heroImage: "/images/interior/classic-elegant-dining-room-with-chandelier.jpg",
        gallery: [
            "https://images.pexels.com/photos/1648776/pexels-photo-1648776.jpeg",
            "https://images.pexels.com/photos/32269122/pexels-photo-32269122.png",
            "https://images.pexels.com/photos/7195737/pexels-photo-7195737.jpeg"
        ],
        features: ["Luxury Materials", "Refined Details", "Timeless Appeal"],
        color: "bg-amber-500",
        accentColor: "text-amber-600"
    },
    {
        id: "contemporary-luxury",
        title: "Contemporary Luxury",
        description: "Bold statements and premium materials create stunning modern spaces",
        heroImage: "/images/interior/contemporary-luxury-bedroom-with-premium-finishes.jpg",
        gallery: [
            "https://images.pexels.com/photos/34165826/pexels-photo-34165826.jpeg",
            "https://images.pexels.com/photos/2343465/pexels-photo-2343465.jpeg",
            "https://images.pexels.com/photos/6969863/pexels-photo-6969863.jpeg"
        ],
        features: ["Premium Finishes", "Bold Statements", "Modern Luxury"],
        color: "bg-indigo-500",
        accentColor: "text-indigo-600"
    },
    {
        id: "organic-modern",
        title: "Organic Modern",
        description: "Natural textures meet modern sensibility for harmonious living",
        heroImage: "/images/interior/organic-modern-interior-with-natural-materials.jpg",
        gallery: [
            "https://images.pexels.com/photos/6186826/pexels-photo-6186826.jpeg",
            "https://images.pexels.com/photos/6782567/pexels-photo-6782567.jpeg",
            "https://images.pexels.com/photos/6758776/pexels-photo-6758776.jpeg"
        ],
        features: ["Natural Materials", "Organic Shapes", "Sustainable Design"],
        color: "bg-green-500",
        accentColor: "text-green-600"
    },
]

export function DesignStyles() {
    const [selectedStyle, setSelectedStyle] = useState(styles[0])

    return (
        <section className="py-24 lg:py-32 bg-gradient-to-b from-background to-muted/20">
            <div className="container mx-auto px-4 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <Badge variant="outline" className="mb-4 text-sm font-medium">
                        Design Mastery
                    </Badge>
                    <h2 className="text-4xl md:text-6xl font-heading font-bold text-foreground mb-6 text-balance">
                        Where Vision Meets
                        <span className="block bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                            Mastery
                        </span>
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-pretty leading-relaxed">
                        Our design expertise spans every aesthetic, from timeless elegance to cutting-edge contemporary.
                        Each style is crafted with precision, passion, and an unwavering commitment to excellence.
                    </p>
                </div>

                {/* Hero Image Section */}
                <div className="relative mb-16">
                    <div className="aspect-[21/9] relative overflow-hidden rounded-3xl shadow-2xl">
                        <img
                            src={selectedStyle.heroImage || "/placeholder.svg"}
                            alt={`${selectedStyle.title} Design Style`}
                            className="w-full h-full object-cover transition-all duration-1000"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                        {/* Style Indicator Dots */}
                        <div className="absolute top-8 right-8 flex flex-col gap-3">
                            {styles.map((style) => (
                                <button
                                    key={style.id}
                                    onClick={() => setSelectedStyle(style)}
                                    className={`w-4 h-4 rounded-full border-2 border-white transition-all duration-300 ${selectedStyle.id === style.id
                                            ? `${style.color} shadow-lg scale-125`
                                            : 'bg-white/30 hover:bg-white/50'
                                        }`}
                                    aria-label={`Select ${style.title} style`}
                                />
                            ))}
                        </div>

                        {/* Hero Content Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16 text-white">
                            <div className="max-w-4xl">
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {selectedStyle.features.map((feature, index) => (
                                        <Badge key={index} variant="secondary" className="text-sm bg-white/20 text-white border-white/30 backdrop-blur-sm">
                                            {feature}
                                        </Badge>
                                    ))}
                                </div>
                                <h3 className="text-5xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
                                    {selectedStyle.title}
                                </h3>
                                <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-2xl">
                                    {selectedStyle.description}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Style Selector - Desktop */}
                <div className="hidden lg:block mb-16">
                    <div className="flex justify-center gap-8">
                        {styles.map((style) => (
                            <button
                                key={style.id}
                                onClick={() => setSelectedStyle(style)}
                                className={`group relative px-8 py-4 rounded-2xl transition-all duration-300 ${selectedStyle.id === style.id
                                        ? `${style.color} text-white shadow-lg scale-105`
                                        : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <span className="font-semibold text-lg">{style.title}</span>
                                {selectedStyle.id === style.id && (
                                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Style Selector - Mobile */}
                <div className="lg:hidden mb-16">
                    <Select value={selectedStyle.id} onValueChange={(value) => {
                        const style = styles.find(s => s.id === value)
                        if (style) setSelectedStyle(style)
                    }}>
                        <SelectTrigger className="w-full max-w-md mx-auto">
                            <SelectValue placeholder="Select a design style" />
                        </SelectTrigger>
                        <SelectContent>
                            {styles.map((style) => (
                                <SelectItem key={style.id} value={style.id}>
                                    {style.title}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Dynamic Gallery Section */}
                <div className="mb-16">
                    <div className="text-center mb-12">
                        <h3 className="text-3xl font-heading font-bold mb-4">
                            {selectedStyle.title} Gallery
                        </h3>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Explore our curated collection showcasing the essence of {selectedStyle.title.toLowerCase()} design.
                        </p>
                    </div>

                    {/* Desktop Gallery Grid */}
                    <div className="hidden lg:grid grid-cols-3 gap-6">
                        {selectedStyle.gallery.map((image, index) => (
                            <div key={index} className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
                                <div className="aspect-[4/3] relative overflow-hidden">
                                    <img
                                        src={image || "/placeholder.svg"}
                                        alt={`${selectedStyle.title} example ${index + 1}`}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Mobile Gallery Carousel */}
                    <div className="lg:hidden">
                        <Carousel className="w-full max-w-md mx-auto">
                            <CarouselContent>
                                {selectedStyle.gallery.map((image, index) => (
                                    <CarouselItem key={index}>
                                        <div className="relative overflow-hidden rounded-2xl shadow-lg">
                                            <div className="aspect-[4/3] relative overflow-hidden">
                                                <img
                                                    src={image || "/placeholder.svg"}
                                                    alt={`${selectedStyle.title} example ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            <CarouselPrevious className="left-4" />
                            <CarouselNext className="right-4" />
                        </Carousel>
                    </div>
                </div>

                {/* Call to Action */}
                <div className="text-center">
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-3xl p-12 lg:p-16">
                        <h3 className="text-3xl lg:text-4xl font-heading font-bold mb-6">
                            Ready to Transform Your Space?
                        </h3>
                        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                            Let our design mastery bring your vision to life. From concept to completion,
                            we create spaces that inspire and delight.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button size="lg" className="px-8 py-4 text-lg">
                                View Our Portfolio
                            </Button>
                            <Button variant="outline" size="lg" className="px-8 py-4 text-lg">
                                Schedule Consultation
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
